import { useMondayItemManager } from "../components/monday-item-manager-context";
import { useEffect, useState } from "react";
import { meetsFilterCriteria } from "frontend/hooks/use-board-integrations";
import { ListKey } from "../services/monday-item.types";

interface StateType {
  readonly serverItemIds: undefined | string[];
  readonly hasMore: boolean;
  readonly isLoading: boolean;
  readonly add?: Set<string>;
  readonly remove?: Set<string>;
}

//TODO: sometimes I have data (from cache) and I'm also asking the server for validation
// I should represent this in my state and the UI
export function useMondayLiveList(
  integrationId: string | null | undefined,
  board: any,
  filters: any
): {
  data: any;
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  error: Error | undefined;
} {
  const ctx = useMondayItemManager();
  const key = new ListKey(integrationId, board?.id, filters);

  // Initial state is empty, and loading is set to true only if we are connected to a monday board
  const [state, setState] = useState<StateType>({
    serverItemIds: undefined,
    hasMore: false,
    isLoading: !!integrationId,
  });

  // fetch and subscribe to data when we're connected to a board
  useEffect(() => {
    if (board?.id) {
      const myId = board.id;
      // todo: check all current items in data store if they match5
      let ids: string[] = [];
      for (const item of ctx.allItems()) {
        if (item?.data) {
          if (item.data?.board.id == myId) {
            const shouldInclude = meetsFilterCriteria(item.data, filters, board?.columns);
            if (shouldInclude) {
              ids.push(item!.data.id!);
            }
          }
        }
      }
      setState({ serverItemIds: ids, hasMore: false, isLoading: true });
      ctx.fetchLiveListAndSubscribe(key, ({ itemIds, hasMore }: { itemIds: string[]; hasMore: boolean }) =>
        setState({ serverItemIds: itemIds, hasMore, isLoading: false })
      );
    }
  }, [board?.id, filters]);

  // Listen to changes in items of our board - only if we have filters.
  // Then we filter items by their (optimistic) changes
  useEffect(() => {
    if (!key.boardId || !filters?.length) {
      return;
    }
    return ctx.subscribeToAnyItemChange((itemId, { data }) => {
      if (data?.board.id != key.boardId) {
        return;
      }
      const shouldInclude = meetsFilterCriteria(data, filters, board?.columns);
      if (shouldInclude) {
        setState((prev: any) => {
          let { add, remove, serverItemIds } = prev;
          add ||= new Set();
          remove ||= new Set();
          add.add(itemId);
          remove.delete(itemId);
          return { ...prev, serverItemIds, add, remove };
        });
      } else {
        setState((prev: any) => {
          let { add, remove, serverItemIds } = prev;
          add ||= new Set();
          remove ||= new Set();
          remove.add(itemId);
          add.delete(itemId);
          return { ...prev, serverItemIds, add, remove };
        });
      }
    });
  }, [key.boardId, filters]);

  let final = undefined;
  if (state.serverItemIds) {
    final = state.serverItemIds;
    if (state.remove) {
      final = final.filter((id: any) => !state.remove!.has(id));
    }
    if (state.add) {
      final = [...final, ...state.add];
    }
    final = [...new Set(final)];
  }

  return {
    data: final,
    isLoading: state.isLoading,
    hasMore: state.hasMore,
    loadMore: () => {
      setState((state: any) => ({ ...state, isLoading: true }));
      ctx.loadMore(key);
    },
    error: undefined,
  };
}
